export const DEFAULT_ERROR_MESSAGE = '操作失败，请稍后重试'
export const DEFAULT_SERVER_ERROR_MESSAGE = '服务器繁忙，请稍后重试'
export const DEFAULT_NETWORK_ERROR_MESSAGE = '网络异常，请检查网络后重试'
export const DEFAULT_TIMEOUT_ERROR_MESSAGE = '请求超时，请稍后重试'

const SENSITIVE_MESSAGE_PATTERNS = [
  /\b(?:NullPointerException|SQLException|SQLSyntaxErrorException|DataIntegrityViolationException|RuntimeException|IllegalStateException|ClassCastException|StackOverflowError)\b/i,
  /\b(?:java|javax|jakarta|org\.springframework|org\.hibernate|com\.mysql|cn\.iselab)\.[A-Za-z0-9_.$]+/i,
  /\b(?:SQLSTATE|JDBC|Hibernate|MySQL|MariaDB|PostgreSQL|Redis|syntax error|constraint \[|could not execute statement)\b/i,
  /(?:^|\n)\s*at\s+[A-Za-z0-9_.$]+\(.*?:\d+\)/,
  /\bCaused by:\b/i,
  /(?:[A-Za-z]:\\|\/(?:var|tmp|home|usr|opt|app|workspace|data)\/)[^\s"'<>]+/
]

function normalizeText(value) {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'string') {
    return value.trim()
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return ''
}

function isDiagnosticLongText(message) {
  const lines = message.split(/\r?\n/)
  if (lines.length > 2) {
    return true
  }
  return message.length > 240 && /[:=]|Exception|SQL|JDBC|org\.|java\.|cn\./i.test(message)
}

export function isSensitiveErrorMessage(message) {
  const text = normalizeText(message)
  if (!text) {
    return false
  }
  if (isDiagnosticLongText(text)) {
    return true
  }
  return SENSITIVE_MESSAGE_PATTERNS.some(pattern => pattern.test(text))
}

export function sanitizeDisplayMessage(message, fallback = DEFAULT_ERROR_MESSAGE) {
  const text = normalizeText(message)
  const fallbackText = fallback === '' ? '' : (normalizeText(fallback) || DEFAULT_ERROR_MESSAGE)
  if (!text) {
    return fallbackText
  }
  if (isSensitiveErrorMessage(text)) {
    return fallbackText
  }
  return text
}

function getStatus(error) {
  const status = Number(error?.response?.status ?? error?.status ?? error?.response?.data?.code)
  return Number.isFinite(status) ? status : 0
}

function isTimeoutError(error) {
  const code = normalizeText(error?.code)
  const message = normalizeText(error?.message).toLowerCase()
  return code === 'ECONNABORTED' || code === 'ETIMEDOUT' || message.includes('timeout')
}

function isNetworkError(error) {
  const code = normalizeText(error?.code)
  return !error?.response || code === 'ERR_NETWORK' || code === 'ENETUNREACH'
}

function getResponseMessage(error) {
  const data = error?.response?.data
  if (data && typeof data === 'object') {
    return data.message ?? data.reason ?? ''
  }
  return data || error?.message || ''
}

function fallbackForStatus(status) {
  if (status >= 500) {
    return DEFAULT_SERVER_ERROR_MESSAGE
  }
  switch (status) {
    case 401:
      return '请先登录'
    case 403:
      return '没有权限访问'
    case 404:
      return '请求资源不存在'
    case 413:
      return '上传文件过大，超过服务端最大限制'
    case 429:
      return '请求过于频繁，请稍后重试'
    default:
      return DEFAULT_ERROR_MESSAGE
  }
}

function isBlobLike(value) {
  return typeof Blob !== 'undefined' && value instanceof Blob
}

export function getSafeErrorMessage(error, fallback = DEFAULT_ERROR_MESSAGE) {
  if (isTimeoutError(error)) {
    return DEFAULT_TIMEOUT_ERROR_MESSAGE
  }
  if (isNetworkError(error)) {
    return DEFAULT_NETWORK_ERROR_MESSAGE
  }

  const status = getStatus(error)
  if (status >= 500) {
    return DEFAULT_SERVER_ERROR_MESSAGE
  }

  const fallbackText = normalizeText(fallback) || fallbackForStatus(status)
  return sanitizeDisplayMessage(getResponseMessage(error), fallbackText)
}

export function sanitizeApiError(error) {
  if (!error) {
    return error
  }

  const safeMessage = getSafeErrorMessage(error)
  const status = getStatus(error)

  if (error.response) {
    const data = error.response.data
    if (data && typeof data === 'object' && !isBlobLike(data)) {
      error.response.data = {
        ...data,
        message: safeMessage
      }
      if (Object.prototype.hasOwnProperty.call(data, 'reason')) {
        error.response.data.reason = safeMessage
      }
    } else {
      error.response.data = {
        code: status || 0,
        message: safeMessage,
        data: null
      }
    }
  } else {
    error.response = {
      status: status || 0,
      data: {
        code: status || 0,
        message: safeMessage,
        data: null
      }
    }
  }

  try {
    error.message = safeMessage
    error.safeMessage = safeMessage
  } catch {
    // Some error objects can expose readonly fields; response.data has already been sanitized.
  }

  return error
}
